import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from '@emotion/styled'
import { Address } from '@open-tender/types'
import { inZone } from '@open-tender/utils'
import {
  closeModal,
  selectApi,
  selectOrder,
  selectSettings,
  setAddress,
  useAppDispatch,
  useAppSelector,
} from '@open-tender/cloud'
import {
  Button,
  FormError,
  GoogleMap,
  GoogleMapsAutocomplete,
} from 'components'
import { ClipLoader } from 'react-spinners'
import { MapsArrowDiagonal } from '../icons'
import { ModalContent, ModalView } from '..'

const MapView = styled.div`
  position: fixed;
  z-index: 0;
  top: 0;
  left: 0;
  width: 0;
  height: 0;
`

const Map = ({ mapRef }: { mapRef: React.RefObject<HTMLDivElement> }) => (
  <MapView>
    <div ref={mapRef} />
  </MapView>
)

const MapsAutocompleteView = styled.div`
  position: relative;
  width: 100%;
  margin: 0 0 24rem;
`

const MapsAutocompleteError = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin: 2rem 0 0;
`

const MapsAutocompleteInputView = styled.div`
  width: 100%;
`

const MapsAutocompleteInput = ({
  maps,
  map,
  sessionToken,
  autocomplete,
  formattedAddress,
  setCenter,
  setAddress,
}: {
  maps?: typeof google.maps | null
  map?: google.maps.Map | null
  sessionToken?: google.maps.places.AutocompleteSessionToken | null
  autocomplete?: google.maps.places.AutocompleteService | null
  formattedAddress: string
  setCenter: ({ lat, lng }: { lat: number; lng: number }) => void
  setAddress: (address: Address | null) => void
}) => {
  if (!maps || !map || !sessionToken || !autocomplete) return null
  return (
    <MapsAutocompleteInputView>
      <GoogleMapsAutocomplete
        maps={maps}
        map={map}
        sessionToken={sessionToken}
        autocomplete={autocomplete}
        formattedAddress={formattedAddress}
        setCenter={setCenter}
        setAddress={setAddress}
        icon={<MapsArrowDiagonal size={14} strokeWidth={2} />}
        placeholder="please enter your address"
      />
    </MapsAutocompleteInputView>
  )
}

const nonInZone =
  'Address not in delivery zone. Please try again or try a different location.'

const MapsAutocomplete = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const api = useAppSelector(selectApi)
  const { googleMaps } = useAppSelector(selectSettings) || {}
  const { apiKey, defaultCenter, zoom, styles } = googleMaps || {}
  const [, setCenter] = useState(defaultCenter)
  const [isValidating, setIsValidating] = useState(false)
  const [error, setError] = useState<any>(null)
  const { address, revenueCenter, orderType, serviceType } =
    useAppSelector(selectOrder)
  const formattedAddress = address ? address.formatted_address : ''
  const { delivery_zone, slug } = revenueCenter || {}
  const { coordinates } = delivery_zone || {}
  const menuSlug = `/menu/${slug}`
  const hasSettings = !!(apiKey && defaultCenter && zoom && styles)

  const applyAddress = async (addr: Address | null) => {
    setError(null)
    dispatch(setAddress(addr))
    if (!addr) return

    const { lat, lng } = addr
    const addrLatLng = lat && lng ? { lat, lng } : null

    // Quick client-side polygon check
    if (!addrLatLng || !coordinates || !inZone(addrLatLng, coordinates)) {
      setError(nonInZone)
      return
    }

    // Validate driving distance via direct API call (no Redux side effects)
    if (api && lat && lng && orderType) {
      setIsValidating(true)
      try {
        const response: any = await api.getLocations(
          orderType,
          false,
          lat,
          lng,
          undefined,
          undefined,
          undefined,
          serviceType ?? undefined,
          50
        )
        const locations = response?.data || []
        const match = locations.find((rc: any) => rc.slug === slug)
        if (!match || match.in_delivery_range === false) {
          setError(nonInZone)
          return
        }
      } catch {
        // API failure — fall back to polygon check (already passed)
      } finally {
        setIsValidating(false)
      }
    }

    dispatch(closeModal())
    navigate(menuSlug)
  }

  return (
    <ModalView>
      <ModalContent
        title="Enter your address"
        footer={<Button onClick={() => dispatch(closeModal())}>Close</Button>}
      >
        <MapsAutocompleteView>
          {hasSettings && (
            <GoogleMap
              apiKey={apiKey}
              zoom={zoom}
              styles={styles}
              center={defaultCenter}
              loader={<ClipLoader size={30} loading={true} />}
              renderMap={(props) => <Map {...props} />}
            >
              <MapsAutocompleteInput
                formattedAddress={formattedAddress ?? ''}
                setCenter={setCenter}
                setAddress={(address) => applyAddress(address)}
              />
            </GoogleMap>
          )}
          <MapsAutocompleteError>
            <FormError errMsg={error} />
          </MapsAutocompleteError>
        </MapsAutocompleteView>
      </ModalContent>
    </ModalView>
  )
}

export default MapsAutocomplete
